import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Star, Heart, Baby, Shield,
  TrendingUp, Clock, MapPin, Sparkles, Filter, ChevronDown
} from "lucide-react";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.6, delay: i * 0.08 } });

const CATEGORIES = ["All", "PCOS", "IVF", "Male Factor", "Thyroid", "Pregnancy", "Couples", "Ayurveda"];

const STORIES = [
  {
    name: "Priya & Karthik M.",
    location: "Bangalore",
    category: ["Couples"],
    condition: "Unexplained Fertility Challenges",
    program: "Fitly Elite — Couples Track (3 Months)",
    timeline: "Conceived in 4 months",
    image: "https://images.unsplash.com/photo-1654764746576-20b2dea2efff?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["3 years of trying", "No clear diagnosis", "Multiple doctor visits"],
    afterStats: ["Natural conception in month 4", "Healthy pregnancy", "Diet-driven improvement"],
    story: "After 3 years of trying with no clear diagnosis, Priya and Karthik were running out of options. Fitly's couples track addressed both their nutritional profiles simultaneously — cycle-synced nutrition for Priya and spermatogenesis-aligned support for Karthik. Within 4 months, they conceived naturally.",
    quote: "The customized diet and Dr. Neelima's guidance changed everything for us!",
    outcome: "Natural Conception",
    color: "#E8B4B8",
  },
  {
    name: "Sneha R.",
    location: "Chennai",
    category: ["PCOS"],
    condition: "PCOS & Irregular Cycles",
    program: "Fitly Elite — Her Track (3 Months)",
    timeline: "Cycles regulated in 2 months",
    image: "https://images.unsplash.com/photo-1718965018802-897e94ce7f15?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["Irregular periods for years", "Hormonal imbalance", "Weight gain"],
    afterStats: ["Regular cycles in 8 weeks", "Lost 8 kgs", "Balanced hormones"],
    story: "Sneha had been battling irregular periods and hormonal imbalance for years. The cycle-synced nutrition protocol combined with gut-hormone axis optimization restored her cycle regularity within just 2 months. The anti-inflammatory diet addressed insulin resistance — a root cause her previous doctors had overlooked.",
    quote: "My cycles became regular, lost 8 kgs, and my hormones are finally balanced. Thank you Mrs. Nadiya and team!",
    outcome: "Cycle Regularity Restored",
    color: "#A8D5BA",
  },
  {
    name: "Anjali & Rahul S.",
    location: "Hyderabad",
    category: ["Couples", "Male Factor"],
    condition: "Low AMH & Male Factor Infertility",
    program: "Fitly Elite — Couples Track (6 Months)",
    timeline: "Conceived naturally — twins!",
    image: "https://images.unsplash.com/photo-1787318479102-eea147892d27?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["Low AMH levels", "Low sperm count", "Advised IVF only"],
    afterStats: ["AMH improved", "Sperm count doubled", "Natural twin pregnancy"],
    story: "Anjali's AMH was critically low and Rahul's sperm analysis showed poor parameters. They were told IVF was their only option. The couples track addressed both — biomarker-informed care for Anjali and spermatogenesis-synced nutrition with andrology input from Dr. Fahim for Rahul. Six months later, they conceived twins naturally.",
    quote: "We were told IVF was our only option. Fitly helped us both — we're now expecting twins naturally!",
    outcome: "Natural Twin Conception",
    color: "#B8C4E8",
  },
  {
    name: "Deepika V.",
    location: "Mumbai",
    category: ["IVF"],
    condition: "Recurrent IVF Failure",
    program: "Fitly Elite — Her Track (3 Months Pre-IVF)",
    timeline: "Successful 3rd IVF cycle",
    image: "https://images.unsplash.com/photo-1760124146298-b1534ee3c9a0?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["2 failed IVF cycles", "Poor egg quality", "Thin endometrial lining"],
    afterStats: ["Improved egg quality", "Optimal lining thickness", "Successful embryo transfer"],
    story: "After two devastating IVF failures, Deepika was emotionally and physically drained. Fitly's pre-IVF protocol focused on improving egg quality through antioxidant-rich nutrition, CoQ10 optimization, and stress management over a full 90-day egg maturation cycle. Her third IVF cycle was successful.",
    quote: "Their pre-IVF nutrition protocol prepared my body perfectly. Third cycle was successful!",
    outcome: "Successful IVF",
    color: "#D4B8E8",
  },
  {
    name: "Meenakshi K.",
    location: "Delhi",
    category: ["Thyroid"],
    condition: "Hypothyroidism & Weight Issues",
    program: "Fitly Elite — Her Track (3 Months)",
    timeline: "TSH normalized in 3 months",
    image: "https://images.unsplash.com/photo-1630001603446-585f8a8d5527?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["Elevated TSH levels", "12 kgs overweight", "2 years trying to conceive"],
    afterStats: ["TSH within normal range", "Lost 12 kgs", "Natural conception"],
    story: "Meenakshi's thyroid imbalance was silently undermining her fertility. The biomarker-informed care protocol identified the connection between her thyroid, insulin resistance, and weight. A targeted nutrition plan — without any crash dieting — brought her TSH into the optimal fertility range and she lost 12 kgs in 3 months.",
    quote: "My thyroid levels normalized and I conceived naturally after struggling for 2 years. Best decision ever!",
    outcome: "Natural Conception",
    color: "#C4B896",
  },
  {
    name: "Lakshmi N.",
    location: "Bangalore",
    category: ["Ayurveda", "Pregnancy"],
    condition: "Recurrent Miscarriage",
    program: "Fitly Premium Plus (6 Months)",
    timeline: "Full-term healthy delivery",
    image: "https://images.unsplash.com/photo-1706153068774-5271417921a4?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["2 miscarriages", "High cortisol levels", "Nutritional deficiencies"],
    afterStats: ["Full-term pregnancy", "Healthy baby boy", "Stable hormones throughout"],
    story: "After two heartbreaking miscarriages, Lakshmi needed more than just a diet — she needed comprehensive care. The Premium Plus program combined Revathy R.P's precision nutrition with Dr. Rekha's Ayurvedic protocols for cortisol management and uterine strength. She carried to full term with monitored nutrition at every stage.",
    quote: "Dr. Rekha's Ayurvedic approach combined with nutrition helped me carry my baby to full term!",
    outcome: "Healthy Full-Term Birth",
    color: "#E8B4B8",
  },
  {
    name: "Fathima & Irfan Z.",
    location: "Dubai (Remote)",
    category: ["Couples"],
    condition: "Unexplained Infertility — International Client",
    program: "Fitly Elite — Couples Track (6 Months)",
    timeline: "Conceived in month 5",
    image: "https://images.unsplash.com/photo-1759495381902-180fd4aa95c2?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["4 years unexplained infertility", "Multiple specialists consulted", "Based outside India"],
    afterStats: ["Root nutritional gaps identified", "Conceived in month 5", "Managed fully online"],
    story: "Living in Dubai with no local fertility nutrition support, Fathima and Irfan joined Fitly's fully online couples program. The 5-pillar assessment revealed nutritional gaps that their previous doctors had missed — low Vitamin D, suboptimal B12, and a disrupted gut-hormone axis. The remote model with WhatsApp support made it seamless.",
    quote: "Nadiya's personalized approach found gaps our doctors missed. Conceived in month 5!",
    outcome: "International Success",
    color: "#A8D5BA",
  },
  {
    name: "Kavitha P.",
    location: "Coimbatore",
    category: ["Ayurveda"],
    condition: "Stage 3 Endometriosis",
    program: "Fitly Elite + Ayurveda Consults (6 Months)",
    timeline: "14 weeks pregnant",
    image: "https://images.unsplash.com/photo-1691495257420-bc31c53097f2?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["Stage 3 endometriosis", "Chronic pelvic pain", "Losing hope"],
    afterStats: ["Pain reduced dramatically", "Inflammation markers down", "Currently 14 weeks pregnant"],
    story: "Kavitha's Stage 3 endometriosis caused chronic pain and made conception seem impossible. The anti-inflammatory nutrition protocol — targeting the gut-hormone axis and cortisol-conscious eating — was combined with Dr. Rekha's Ayurvedic interventions. Her pain reduced dramatically and her inflammation markers dropped significantly.",
    quote: "The anti-inflammatory diet and Ayurveda sessions reduced my pain dramatically. I'm now 14 weeks pregnant!",
    outcome: "Pregnant Despite Endo",
    color: "#B8C4E8",
  },
  {
    name: "Swathi & Arun D.",
    location: "Bangalore",
    category: ["Pregnancy", "Couples"],
    condition: "Full Pregnancy Journey",
    program: "Fitly Premium Plus (9 Months)",
    timeline: "Healthy delivery",
    image: "https://images.unsplash.com/photo-1759495382504-69d5b10ae695?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["First pregnancy", "Anxiety about nutrition", "Wanted end-to-end support"],
    afterStats: ["Trimester-specific plans followed", "Confident delivery", "Healthy baby"],
    story: "Swathi and Arun wanted comprehensive support for their entire pregnancy journey — from conception planning through delivery. The Premium Plus program provided trimester-specific nutrition, weekly monitoring, live yoga 5 days a week, and specialist consultations whenever needed. Their dedicated care coordinator ensured nothing fell through the cracks.",
    quote: "The trimester-wise nutrition plans gave us so much confidence. Our little one is healthy and thriving!",
    outcome: "Healthy Delivery",
    color: "#D4B8E8",
  },
  {
    name: "Roshni G.",
    location: "Pune",
    category: ["PCOS"],
    condition: "Severe PCOS & Insulin Resistance",
    program: "Fitly Elite — Her Track (3 Months)",
    timeline: "Insulin normalized in 3 months",
    image: "https://images.unsplash.com/photo-1764078682636-151f730ccbd4?auto=format&fit=crop&w=200&h=200&crop=faces&q=80",
    beforeStats: ["Fasting insulin: 28", "Severe PCOS symptoms", "Absent periods"],
    afterStats: ["Fasting insulin: 9", "Lost 10 kgs", "Regular cycles restored"],
    story: "Roshni's fasting insulin of 28 was dangerously high — a classic marker of severe insulin resistance driving her PCOS. The biomarker-informed protocol targeted insulin sensitivity through strategic carbohydrate management, circadian meal timing, and gut microbiome restoration. In 3 months, her insulin dropped to 9 and her cycles returned naturally.",
    quote: "Fasting insulin dropped from 28 to 9, lost 10 kgs, and my cycles returned on their own. Truly life-changing!",
    outcome: "PCOS Reversed",
    color: "#C4B896",
  },
];

const Stars = ({ count }) => (
  <div className="flex gap-0.5">
    {[...Array(count)].map((_, i) => <Star key={`star-${i}`} size={12} className="fill-[#D4AF37] text-[#D4AF37]" />)}
  </div>
);

export default function SuccessStoriesPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All"
    ? STORIES
    : STORIES.filter((s) => s.category.includes(activeCategory));

  return (
    <div className="min-h-screen bg-[#0D1F17] text-[#F7F5F0]">
      {/* Header */}
      <header className="border-b border-[#F7F5F0]/5 bg-[#0D1F17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A3B8AD] hover:text-[#F7F5F0] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={18} /><span className="text-sm tracking-wide">Back</span>
          </button>
          <a href="/" className="font-serif text-xl">
            <span className="font-semibold text-[#F7F5F0]">Fitly</span> <span className="font-light text-[#A3B8AD]">India</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Food background image */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1572357176061-7c96fd2af22f?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0D1F17]/[0.85]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #D4AF37 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="absolute top-10 left-20 w-72 h-72 rounded-full bg-[#E8B4B8]/5 blur-[100px]" />
        <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-[#D4AF37]/5 blur-[100px]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#D4AF37]">Real Journeys, Real Outcomes</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-6 mb-6" data-testid="success-stories-title">
              Success <span className="italic font-light text-[#D4AF37]">Stories</span>
            </h1>
            <p className="text-[#F7F5F0]/50 text-sm max-w-xl mx-auto leading-relaxed">
              Every journey is unique. These are real clients who trusted us with their fertility goals — and the outcomes speak for themselves.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-14 flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {[
              { value: "10,000+", label: "Clients Served" },
              { value: "98%", label: "Success Rate" },
              { value: "4.9", label: "Google Rating (114+ reviews)" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-2xl md:text-3xl text-[#D4AF37]">{s.value}</p>
                <p className="text-xs text-[#A3B8AD]/60 mt-1 tracking-wider uppercase">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-[#FEFDFB] text-[#0D1F17] py-8 sticky top-[57px] z-40 border-b border-[#0D1F17]/5" data-testid="story-filters">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide pb-1">
            <Filter size={16} className="text-[#0D1F17]/30 flex-shrink-0" />
            {CATEGORIES.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#0D1F17] text-[#F7F5F0]"
                    : "bg-[#0D1F17]/5 text-[#0D1F17]/60 hover:bg-[#0D1F17]/10"
                }`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stories */}
      <section className="text-[#0D1F17] py-16 md:py-20 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #FEFDFB 0%, #F5F9F6 50%, #FEFDFB 100%)" }} data-testid="stories-grid">
        {/* Left side HD food border */}
        <div className="absolute left-0 top-0 bottom-0 w-[240px] hidden xl:block pointer-events-none">
          <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=600&h=2000&q=80" alt="" className="w-full h-full object-cover opacity-[0.08]" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FEFDFB]" />
        </div>
        {/* Right side HD food border */}
        <div className="absolute right-0 top-0 bottom-0 w-[240px] hidden xl:block pointer-events-none">
          <img src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=600&h=2000&q=80" alt="" className="w-full h-full object-cover opacity-[0.08]" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#FEFDFB]" />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-[#0D1F17]/40 py-20 font-serif text-lg">No stories found for this category.</p>
          ) : (
            <div className="space-y-10">
              {filtered.map((story, i) => (
                <motion.article key={story.name} {...stagger(i)} data-testid={`story-card-${i}`}
                  className="bg-white border border-[#0D1F17]/6 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">

                  {/* Top color bar */}
                  <div className="h-1.5" style={{ backgroundColor: story.color }} />

                  <div className="p-8 md:p-10">
                    {/* Header row */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-11 h-11 rounded-full overflow-hidden border-2 shadow-sm flex-shrink-0" style={{ borderColor: `${story.color}40` }}>
                            <img src={story.image} alt={story.name} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          <div>
                            <h3 className="font-serif text-xl text-[#0D1F17]">{story.name}</h3>
                            <div className="flex items-center gap-3 text-xs text-[#0D1F17]/40 mt-1">
                              <span className="flex items-center gap-1"><MapPin size={10} /> {story.location}</span>
                              <Stars count={5} />
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {story.category.map((c) => (
                            <span key={c} className="text-[10px] tracking-widest uppercase px-3 py-1 rounded-full font-semibold"
                              style={{ backgroundColor: `${story.color}15`, color: story.color, border: `1px solid ${story.color}25` }}>{c}</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="inline-flex items-center gap-2 bg-[#0D1F17] text-[#F7F5F0] px-4 py-2 rounded-full">
                          <Sparkles size={14} className="text-[#D4AF37]" />
                          <span className="text-xs font-semibold tracking-wide">{story.outcome}</span>
                        </div>
                      </div>
                    </div>

                    {/* Condition & Program */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      <div className="bg-[#FEFDFB] border border-[#0D1F17]/5 rounded-xl p-4">
                        <p className="text-[10px] text-[#0D1F17]/30 uppercase tracking-widest mb-1">Condition</p>
                        <p className="text-sm text-[#0D1F17] font-medium">{story.condition}</p>
                      </div>
                      <div className="bg-[#FEFDFB] border border-[#0D1F17]/5 rounded-xl p-4">
                        <p className="text-[10px] text-[#0D1F17]/30 uppercase tracking-widest mb-1">Program</p>
                        <p className="text-sm text-[#0D1F17] font-medium">{story.program}</p>
                      </div>
                      <div className="bg-[#FEFDFB] border border-[#0D1F17]/5 rounded-xl p-4">
                        <p className="text-[10px] text-[#0D1F17]/30 uppercase tracking-widest mb-1">Timeline</p>
                        <p className="text-sm font-medium flex items-center gap-1.5" style={{ color: story.color }}>
                          <Clock size={13} /> {story.timeline}
                        </p>
                      </div>
                    </div>

                    {/* Before / After */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                      <div className="bg-[#0D1F17]/3 border border-[#0D1F17]/5 rounded-xl p-5">
                        <p className="text-[10px] text-[#0D1F17]/30 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#0D1F17]/20" /> Before Fitly
                        </p>
                        <ul className="space-y-2">
                          {story.beforeStats.map((stat) => (
                            <li key={stat} className="flex items-start gap-2 text-sm text-[#0D1F17]/60">
                              <span className="text-[#0D1F17]/20 mt-1.5">—</span> {stat}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="border rounded-xl p-5" style={{ backgroundColor: `${story.color}08`, borderColor: `${story.color}20` }}>
                        <p className="text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: `${story.color}90` }}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: story.color }} /> After Fitly
                        </p>
                        <ul className="space-y-2">
                          {story.afterStats.map((stat) => (
                            <li key={stat} className="flex items-start gap-2 text-sm text-[#0D1F17]/80">
                              <TrendingUp size={14} className="flex-shrink-0 mt-0.5" style={{ color: story.color }} /> {stat}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Story */}
                    <p className="text-sm text-[#0D1F17]/70 leading-[1.8] mb-6">{story.story}</p>

                    {/* Quote */}
                    <div className="bg-[#0D1F17] rounded-xl p-6 flex items-start gap-4">
                      <div className="text-[#D4AF37] font-serif text-4xl leading-none mt-1">&ldquo;</div>
                      <p className="text-sm text-[#F7F5F0]/80 leading-relaxed italic">{story.quote}</p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-28 text-center relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1920&q=80" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0D1F17]/[0.90]" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <Heart size={36} className="text-[#D4AF37] mx-auto mb-5" />
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F7F5F0] mb-3">Your story could be next</h2>
            <p className="text-[#A3B8AD] mb-8 text-sm max-w-md mx-auto">
              Every journey starts with a single step. Book a consultation and let our team understand your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/programs/elite" data-testid="cta-explore-elite"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0D1F17] px-10 py-4 text-sm font-bold tracking-wide rounded-full hover:bg-[#B5952F] transition-all shadow-lg shadow-[#D4AF37]/20">
                Explore Fitly Elite <ArrowRight size={16} />
              </a>
              <a href="/consultation" data-testid="cta-book-consultation"
                className="inline-flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-8 py-4 text-sm font-semibold tracking-wide rounded-full hover:bg-[#D4AF37] hover:text-[#0D1F17] transition-all">
                Book Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
